<!-- eslint-disable vue/require-component-is -->
<template>
  <div class="modal-value-help">
    <TableHeader
      ref="header-ref"
      :attrs="attrs"
      :multiple="multiple"
      :isBusy="isBusy"
      :isEditing="isEditing"
      :totalSelected="selectedValuesLength"
      :search.sync="search"
      @addNewData="handleAdd"
      @unselectAll="handleUnselectAll"
    >
      <template #filter>
        <b-row v-if="filters && filters.length > 0" class="mt-2 mb-4">
          <b-col md="8">
            <b-row>
              <b-col v-for="filter in filters" :key="filter.key" md="6">
                <multiselect
                  v-model="filter.model"
                  :options="filterData[filter.key].data"
                  label="name"
                  track-by="id"
                  :placeholder="filter.label"
                  :multiple="Array.isArray(filter.model) ? true : false"
                  :loading="filterData[filter.key].loading"
                >
                  <template #selection="{ values, isOpen, remove }">
                    <span
                      v-if="Array.isArray(filter.model) && filter.model.length > 0"
                      class="d-flex gap-2 flex-wrap custom__tag"
                    >
                      <div v-for="(value, index) in values" :key="index">
                        <div
                          v-if="index == 0"
                          v-b-tooltip.hover="value?.name?.length > 15 ? value?.name : null"
                          class="__tag"
                        >
                          <span>
                            {{
                              value?.name?.length > 15
                                ? value?.name?.slice(0, 12) + '...'
                                : value?.name
                            }}
                            <i
                              aria-hidden="true"
                              tabindex="1"
                              class="multiselect__tag-icon"
                              @click="remove(value)"
                            ></i>
                          </span>
                        </div>
                        <div v-else-if="index == 1" class="__count">
                          <i class="icon-fill_plus" />
                          <span>{{ filter.model.length - 1 }}</span>
                        </div>
                      </div>
                    </span>
                  </template>
                </multiselect>
              </b-col>
            </b-row>
          </b-col>
          <b-col md="4">
            <div class="d-flex justify-content-end align-items-center">
              <McButton
                variant="primary"
                :text="$t('global.apply')"
                class="me-2"
                @click="handleFilter"
              />
              <McButton
                variant="secondary"
                :text="$t('global.action.resetConfig')"
                @click="handleReset"
              />
            </div>
          </b-col>
        </b-row>
      </template>
      <template v-if="$scopedSlots.customPlaceholderSelected" #customPlaceholderSelected>
        <slot name="customPlaceholderSelected" :totalSelected="selectedValuesLength" />
      </template>
    </TableHeader>

    <div ref="table-container-ref" class="__table-container">
      <FormulateForm v-model="formValues" @submit="handleSubmit">
        <b-table
          ref="table-ref"
          show-empty
          selectable
          :select-mode="multiple ? 'multi' : 'single'"
          :no-select-on-click="isEditingRow !== null"
          :fields="computedFields"
          :items="items"
          :busy="isBusy"
          class="-w-auto text-nowrap sticky-head-table border"
          :tbody-tr-class="tbodyTrClass"
          @row-clicked="handleRowClicked"
        >
          <!-- Slot: Empty -->
          <template #empty>
            <div v-show="!isBusy" class="text-center my-4">Tidak ada data</div>
          </template>

          <template v-if="attrs.trackBy === 'id'" #head(checkbox)>
            <div class="form-check">
              <input
                v-model="selectAll"
                class="form-check-input me-1"
                type="checkbox"
                value="all"
                @input="toggleAll"
              />
            </div>
          </template>
          <!-- Slot: Cell Checkbox -->
          <template #cell(checkbox)="{ index, item }">
            <div class="form-check">
              <b-form-checkbox
                v-model="selectedValues"
                :value="item[attrs.trackBy]"
                @change="handleCheckboxChange(item, index)"
              />
            </div>
          </template>

          <!-- Table Header -->
          <template #head()="data">
            <div @click="handleSortChange(data)">
              {{ data.label }}
              <template v-if="data.field.isSortable">
                <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                  <i class="mc-icon icon-outline_chevron-up"></i>
                </div>
                <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                  <i class="mc-icon icon-outline_chevron-down"></i>
                </div>
              </template>
            </div>
          </template>

          <!-- Table Data -->
          <template #cell()="{ index, field, value, item }">
            <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
              <FormulateInput
                v-bind="field"
                :rowItem="item"
                :label="null"
                :type="field.type || 'text'"
                :disabled="isSubmitting"
                :help="field?.validation?.includes('required')"
                size="sm"
                validation-behavior="live"
                error-behavior="live"
              />
            </template>

            <template v-else>
              <slot v-if="field.isSlot" :name="field.name" :item="item">
                {{ value }}
              </slot>
              <template v-else>{{ value }}</template>
            </template>
          </template>

          <!-- Slot: Action Column (Last Column) -->
          <template #cell(action)="data">
            <div v-if="isEditingRow == data.index">
              <b-button variant="outline" class="__accept-button btn" type="submit">
                <i class="mc-icon icon-fill_checkmark"></i>
              </b-button>

              <b-button
                variant="outline"
                class="__cancel-button btn"
                @click="handleEditCancel(data)"
              >
                <i class="mc-icon icon-outline_close"></i>
              </b-button>
            </div>
          </template>
        </b-table>
      </FormulateForm>

      <!-- Observe visibility to get next page data -->
      <div v-observe-visibility="handleElementObserved" style="height: 1px"></div>

      <!-- Skeleton when getting the data -->
      <div v-if="isBusy && hasNextPage" class="px-2 py-2 d-flex gap-2">
        <b-skeleton width="2.5%" />
        <b-skeleton width="97.5%" />
      </div>
    </div>

    <!-- TODO: Enhance custom create modal in next feature development -->
    <component v-if="attrs['has:createModal']" v-bind="attrs" :is="attrs.customCreateModal" />
  </div>
</template>

<script>
import TableHeader from './Header'
import isEmpty from 'lodash/isEmpty'
import { Helper } from '@/mixin/helper'
import Multiselect from 'vue-multiselect'
import McButton from '@/components/McButton'

export default {
  components: {
    TableHeader,
    Multiselect,
    McButton,
  },
  mixins: [Helper],
  props: {
    isBusy: {
      type: Boolean,
      default: false,
    },

    isEditing: {
      type: Boolean,
      default: false,
    },

    selectedModel: {},

    fullObject: {},

    totalCount: {},

    attrs: {},

    listGetter: {},

    multiple: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:isBusy', 'update:totalCount', 'update:isEditing'],
  data() {
    return {
      items: [],
      ids: [],
      sorts: [],
      page: 1,
      limit: 10,
      search: '',
      total: 0,

      selectedValues: [],
      selectedObject: {},

      formValues: {},
      isEditingRow: null,
      isSubmitting: false,
      selectAll: false,
      selected: this.selectedModel,
      objects: this.fullObject,
      filters: [],
      filterBusy: true,
      filterValue: null,
    }
  },
  computed: {
    computedFields() {
      if (!this.attrs.tableFields) return []

      const fields = [...this.attrs.tableFields]
      if (this.multiple) {
        fields.unshift({
          key: 'checkbox',
          label: '',
          thStyle: 'width:20px;',
        })
      }

      if (this.isEditingRow !== null) {
        fields.push({
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        })
      }

      return fields
    },
    hasNextPage() {
      // If totalCount is null or undefined The data has not been fetched yet
      // so we return true to get the actual total count
      if (!this.totalCount) return true
      return this.page * this.limit < this.totalCount
    },
    selectedValuesLength() {
      return this.selectedValues.length
    },
    defaultDataOptions() {
      return {
        limit: this.limit,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => {
          return { [sort.key]: sort.order }
        }),
        sortArray: this.sorts,
        filters: this.filterValue ? this.filterValue : undefined,
      }
    },
    filterData() {
      const data = {}
      this.filters.forEach((filter) => {
        data[filter.key] = {
          data: this.filterBusy ? [] : filter.data,
          loading: this.filterBusy,
        }
      })
      return data
    },
  },
  watch: {
    async search(newValue) {
      const opts = {
        limit: this.limit,
        search: newValue != '' ? newValue : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => {
          return { [sort.key]: sort.order }
        }),
        sortArray: this.sorts,
        filters: this.filters.filter((item) => item.model.length > 0),
      }
      await this.getData(opts, true)
    },
  },
  async mounted() {
    this.reloadValue()
    this.filters = this.attrs.filters ? [...this.attrs.filters] : []
    if (this.filters.length > 0) {
      this.filterBusy = true
      const promiseAll = []
      this.filters.forEach((filter) => {
        promiseAll.push(filter.data)
      })
      const result = await Promise.all(promiseAll)
      this.filters.forEach((filter, index) => {
        this.filters[index].data = result[index]
      })
      this.filterBusy = false
    }
  },
  beforeDestroy() {
    this.handleHide()
  },
  methods: {
    handleReset() {
      this.filters.forEach((filter) => {
        filter.model = Array.isArray(filter.model) ? [] : ''
      })
      this.getData(
        {
          page: 1,
          ...this.defaultDataOptions,
        },
        true
      )
    },
    handleFilter() {
      this.filterValue = this.filters.filter((item) => item.model.length > 0)
      this.getData(
        {
          page: 1,
          ...this.defaultDataOptions,
        },
        true
      )
    },
    reloadValue() {
      this.selectedValues = []
      if (this.multiple && isEmpty(this.selected)) return
      if (this.selected == '' || this.selected == null) return

      const selected = this.multiple ? this.selected : [this.selected]
      // set keys in selectedValues
      selected.forEach((value) => {
        this.selectedValues.push(value)
      })

      // if there is no fullObject from parent
      if (isEmpty(this.objects) || this.objects == undefined) return

      const objects = this.multiple ? this.objects : [this.objects]

      // set fullObject in selectedObject
      objects.forEach((obj) => {
        this.$set(this.selectedObject, obj[this.attrs.trackBy], obj)
      })
      this.selected = []
      this.objects = {}
    },
    toggleAll(event) {
      let checked = event.target.checked

      if (checked) {
        this.handleSelectAll()
      } else {
        this.handleUnselectAll()
      }
    },
    sortData(items, columnsToSort) {
      items.sort((a, b) => {
        let orderResult = 0
        for (let cts of columnsToSort) {
          const { key, order } = cts
          const modifier = order === 'asc' ? 1 : -1

          let data1 = a[key]
          let data2 = b[key]

          // Actual sorting
          if (typeof data1 === 'string' && typeof data2 === 'string') {
            const compare = data1.localeCompare(data2)
            orderResult = compare * modifier
            if (compare !== 0) break
          } else if (data1 < data2 || (data1 === null && data2 !== null)) {
            orderResult = -1 * modifier
            break
          } else if (data1 > data2 || (data1 !== null && data2 === null)) {
            orderResult = 1 * modifier
            break
          }
        }

        return orderResult
      })
    },
    handleAdd() {
      if (this.attrs['has:createModal']) {
        this.$bvModal.show(`${this.attrs.name}-create-data-modal`)
        return
      }

      this.$emit('update:isEditing', true)

      if (this.isEditingRow != null) return
      this.isEditingRow = 0

      this.items = [{ id: 0 }, ...this.items]
    },
    HandleApply() {
      return {
        selectedValues: this.selectedValues,
        selectedObject: this.selectedObject,
      }
    },
    transformFilter(filter) {
      filter.pagination = {
        show: filter.limit ?? 10,
        page: filter.page ?? 1,
      }
      filter.orderBy =
        filter.sorts && filter.sorts.length > 0 ? JSON.stringify(filter.sorts) : undefined
      if (filter.filters && filter.filters.length > 0) {
        filter.filters.forEach((item) => {
          filter[item.key] = item.model.map((mod) => mod.id)
        })
      }

      delete filter.limit
      delete filter.page
      delete filter.sorts
      delete filter.sortArray
      delete filter.filters

      return filter
    },
    handleHide() {
      this.items = []
      this.ids = []
      this.sorts = []
      this.page = 1
      this.search = ''
      this.filters.forEach((item) => (item.model = Array.isArray(item.model) ? [] : ''))

      this.isEditingRow = null
      this.isSubmitting = false
    },
    async getData(opts, reset = false) {
      this.$emit('update:isBusy', true)

      if (!this.listGetter) {
        this.$emit('update:isBusy', false)
        return
      }

      const { sortArray, ...otherOpts } = opts

      const filter = this.transformFilter(otherOpts)
      const res = await this.listGetter(filter)
      if (!res || res.errors) return

      this.items = reset ? res.data : [...this.items, ...res.data]
      this.page = res.pagination.page
      this.ids = res.ids
      this.total = res.pagination.total
      this.limit = res.pagination.show ?? 10
      this.$emit('update:totalCount', res.pagination.total)

      if (opts.search && this.attrs.searchFunction) {
        this.items = this.attrs.searchFunction(this.items, opts.search)
        this.ids = this.items.map((item) => item[this.attrs.trackBy])
        this.total = this.items.length
        this.$emit('update:totalCount', this.items.length)
      }

      if (opts.sortArray.length > 0) this.sortData(this.items, sortArray)

      this.$emit('update:isBusy', false)
      if (this.total > 0 && this.selectedValues.length >= this.total) {
        this.selectAll = true
      } else {
        this.selectAll = false
      }

      await this.$nextTick()

      if (this.isEditing) return
      this.highlightSelectedRow()
    },
    // highlight the row if there is selected value from parent
    highlightSelectedRow() {
      const trackBy = this.attrs.trackBy

      this.selectedValues.forEach((value) => {
        // intentionally used == instead of === because selectedModel can be a string or number (id or uuid)
        const index = this.items.findIndex((item) => item[trackBy] == value)

        // Toggle the row checkbox
        if (index !== -1) {
          this.$refs['table-ref'].selectRow(index)
        }
      })
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    async handleSortChange(data) {
      // Sorting function is disabled when editing table
      if (this.isEditing) return

      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData(
        {
          page: 1,
          ...this.defaultDataOptions,
        },
        true
      )
    },
    handleRowClicked(item, index) {
      if (!this.multiple) {
        this.selectedValues = []
        this.selectedObject = {}
      }

      if (this.attrs.hasStoreAction && this.isEditingRow !== null) return

      const trackBy = this.attrs.trackBy
      const checked = this.selectedValues.find((val) => val == item[trackBy])

      if (this.attrs.rowCanClick) {
        const isCanClick = this.attrs.rowCanClick(item[this.attrs.trackBy])
        if (!isCanClick) {
          this.$refs['table-ref'].unselectRow(index)
          return
        }
      }

      if (checked) {
        this.$refs['table-ref'].selectRow(index)
        this.selectedValues = this.selectedValues.filter((val) => val !== item[trackBy])
        this.$delete(this.selectedObject, item[trackBy])
        return
      }
      this.selectedValues.push(item[trackBy])
      this.$refs['table-ref'].unselectRow(index)
      this.$set(this.selectedObject, item[trackBy], item)
    },
    handleCheckboxChange(item, index) {
      if (!this.multiple) {
        this.selectedObject = {}
      }

      const trackBy = this.attrs.trackBy
      const checked = this.selectedValues.find((val) => val == item[trackBy])

      if (this.attrs.rowCanClick && checked) {
        const isCanClick = this.attrs.rowCanClick(item[trackBy])
        if (!isCanClick) {
          this.selectedValues = this.selectedValues.filter((val) => val !== item[trackBy])
          this.$refs['table-ref'].unselectRow(index)
          return
        }
      }

      if (!checked) {
        this.$refs['table-ref'].unselectRow(index)
        this.$delete(this.selectedObject, item[trackBy])
        if (this.total > 0 && this.selectedValues.length >= this.total) {
          this.selectAll = true
        } else {
          this.selectAll = false
        }
        return
      }
      this.$refs['table-ref'].selectRow(index)
      this.$set(this.selectedObject, item[trackBy], item)
      if (this.total > 0 && this.selectedValues.length >= this.total) {
        this.selectAll = true
      } else {
        this.selectAll = false
      }
    },
    handleUnselectAll() {
      this.selectAll = false
      if (this.attrs.trackBy === 'id') {
        this.items.forEach((item) => {
          delete this.selectedObject[item.id]
        })
        this.selectedValues = this.selectedValues.filter((id) => !this.ids.includes(id))
      } else {
        this.selectedValues = []
        this.selectedObject = {}
      }
      this.$refs['table-ref'].clearSelected()
    },
    // TODO: Discuss with backend how to handle
    // select all when trackBy is not id
    handleSelectAll() {
      const trackBy = this.attrs.trackBy

      if (trackBy === 'id') {
        this.selectAll = true
        // TODO: handle assign the full object in select all
        this.ids.forEach((id) => {
          if (this.attrs.rowCanClick) {
            const isCanClick = this.attrs.rowCanClick(id)
            if (!isCanClick) return
          }
          if (!this.selectedValues.includes(id)) this.selectedValues.push(id)
          const obj = this.items.find((item) => item[trackBy] === id) ?? {
            id,
          }
          this.$set(this.selectedObject, id, obj)
        })
        this.$refs['table-ref'].selectAllRows()
      } else {
        throw new Error('Select all is not yet implemented when trackBy is not id')
      }
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    async submitStore(data) {
      const createdData = await this.attrs.apiClient.store(data)
      if (createdData.errors) return createdData

      const refetchedData = await this.attrs.apiClient.show(createdData.data.id)
      if (refetchedData.errors) return refetchedData

      this.ids.push(refetchedData.data.id)
      this.$emit('update:totalCount', this.total + 1)

      this.$mcToast.success('Data berhasil ditambahkan')

      return {
        ...data,
        ...refetchedData.data,
      }
    },
    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      this.isSubmitting = true

      const transformedData = this.transformFields(data, this.computedFields)

      let newData = null
      newData = await this.submitStore(transformedData)

      if (newData.errors) {
        this.isSubmitting = false
        return
      }

      // Replace data on the table
      if (newData !== null) {
        this.items.splice(this.isEditingRow, 1, newData)
      }

      this.isEditingRow = null
      this.isSubmitting = false
      await this.$nextTick()

      this.handleRowClicked(newData, 0)

      // to highlight the new row
      this.$refs['table-ref'].selectRow(0)
      this.$emit('update:isEditing', false)
    },
    async handleEditCancel() {
      if (this.isSubmitting) return

      const [, ...newItems] = this.items
      this.items = newItems
      this.isEditingRow = null
      await this.$nextTick()

      this.highlightSelectedRow()
      this.$emit('update:isEditing', false)
    },
    handleElementObserved(isVisible) {
      if (!isVisible) return
      if (!this.hasNextPage) return
      // if there is no items and has the search query
      // then we don't have to get data by observer
      if (this.items.length == 0 && this.search) return

      // if total count is null, it means that we haven't fetch the data (initial)
      // do not increment page, but still fetch the data
      this.getData({
        page: this.total ? ++this.page : this.page,
        ...this.defaultDataOptions,
      })
    },
    tbodyTrClass(item, type) {
      if (this.attrs.rowCanClick) {
        const isCanClick = this.attrs.rowCanClick(item?.[this.attrs?.trackBy])
        if (!isCanClick) return 'disabled'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.__table-container {
  border: 1px solid #ebecef;
  height: 300px;
  overflow-y: auto;

  :deep(.table) {
    --bs-table-active-bg: #e9f2fc;

    thead {
      tr {
        th {
          position: relative;
          &:has(.sort-icon-up),
          &:has(.sort-icon-down) {
            padding-right: 30px;
          }

          .sort-icon-up {
            position: absolute;
            right: 10px;
            top: 8px;
            opacity: 0.7;
          }
          .sort-icon-down {
            position: absolute;
            right: 10px;
            bottom: 8px;
            opacity: 0.7;
          }
        }
      }
    }
    tbody {
      tr.disabled {
        --bs-table-accent-bg: #e6e7ec;
        cursor: not-allowed;
        input[type='checkbox'] {
          cursor: not-allowed;
          background-color: #e6e7ec;
        }
      }
    }

    // Action column
    .mc-action-col-head {
      width: 80px;
    }

    // Action cell
    .mc-action-cell {
      vertical-align: middle;

      & > div {
        display: flex;
        padding: 0 4px;
        justify-content: space-between;
      }

      .mc-icon:before {
        font-size: 20px;
      }

      .btn {
        padding: 0;
      }

      .btn:hover {
        border-color: transparent;
      }

      .__cancel-button .mc-icon:before {
        color: $danger;
      }

      .__disabled .mc-icon:before {
        opacity: $btn-disabled-opacity;
        cursor: not-allowed;
      }

      .__accept-button .mc-icon:before {
        color: $success;
      }
    }
  }
}

.multiselect__tags {
  .__tag {
    padding-right: 24px !important;
    position: relative;
    background-color: $mep-primary;
    color: $white;
    padding-left: 4px !important;
    padding: 2px;
    border-radius: 4px;
    .multiselect__tag-icon {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}

.__count {
  background: $green;
  color: $white;
  padding: 0px 8px !important;
  border-radius: 15px;
  font-size: 16px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 8px !important;

  span {
    font-size: 16px !important;
  }

  i {
    margin: 0;
    padding: 0;

    &::before {
      color: $white !important;
      font-size: 16px !important;
    }
  }
}
</style>
